"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import type { Dictionary } from "@/dictionaries/types";
import type { NftItem, NftsResponse } from "@/features/nfts/types";
import type { CollectionsResponse } from "@/features/collections/types";
import { getAuctions } from "@/features/auctions/api";
import { useGalleries } from "@/features/galleries/hooks/useGalleries";
import { useInfiniteGrid } from "@/hooks/useInfiniteGrid";
import { useInfiniteScrollSentinel } from "@/hooks/useInfiniteScrollSentinel";
import { useQuery } from "@tanstack/react-query";
import { CircleAlert, FolderOpen, Gavel, GalleryVerticalEnd, LayoutGrid, Sparkles } from "lucide-react";
import { normalizeDrop } from "@/features/drops/normalize";
import { canViewDrop } from "@/features/drops/permissions";
import type { Drop } from "@/features/drops/types";
import DropTile from "@/features/drops/v2/DropTile";
import { dropService } from "@/services/drop.service";
import { DeskEmpty } from "@/features/home-v2/desk/Desk";
import { Loading, Spinner } from "@/features/home-v2/ui";
import NftTile from "@/features/home-v2/NftTile";
import { AuctionTile, CollectionTile, GalleryTile, type TileAuction, type TileCollection, type TileGallery } from "@/features/home-v2/tiles";
import { useGridColumns } from "@/hooks/useGridColumns";
import { fullRows } from "@/lib/grid-rows";
type Lang = "fa" | "en";

/**
 * The profile's public tabs. Each is the same three-way state as every other
 * v2 grid — loading, failed (with a retry), empty (with a way out) — plus a
 * scroll sentinel and a real button for the next page, because a sentinel
 * alone cannot be reached from a keyboard.
 *
 * ONLY `username` CROSSES THE SERVER→CLIENT BOUNDARY, NEVER THE ENDPOINT.
 * Every prop a Server Component hands a client one is serialised into the
 * HTML, and an `apiEndpoint="/artists/my-nfts/zahrara"` prop was read by
 * Googlebot as a site link: two phantom URLs per profile, ~8,000 in all, the
 * bulk of a failed Search Console validation. So the endpoint is built here,
 * on the client side of the boundary.
 */

function retryButton(label: string, onRetry: () => void) {
  return <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={onRetry}>{label}</button>;
}

/** A tab that failed to load — the desk's empty page in its failure tone. */
function Failed(t0) {
  const $ = _c(8);
  const {
    dict,
    onRetry
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CircleAlert />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = dict.states.errorTitle;
  const t3 = dict.states.errorBody;
  let t4;
  if ($[1] !== dict.states.retry || $[2] !== onRetry) {
    t4 = retryButton(dict.states.retry, onRetry);
    $[1] = dict.states.retry;
    $[2] = onRetry;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== dict.states.errorBody || $[5] !== dict.states.errorTitle || $[6] !== t4) {
    t5 = <DeskEmpty tone="error" icon={t1} title={t2} body={t3} actions={t4} />;
    $[4] = dict.states.errorBody;
    $[5] = dict.states.errorTitle;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function MoreControl(t0) {
  const $ = _c(11);
  const {
    lang,
    hasMore,
    busy,
    onMore,
    sentinelRef
  } = t0;
  if (!hasMore) {
    return null;
  }
  const t1 = busy || undefined;
  let t2;
  if ($[0] !== busy) {
    t2 = busy ? <Spinner /> : null;
    $[0] = busy;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = lang === "fa" ? "\u0645\u0648\u0627\u0631\u062F \u0628\u06CC\u0634\u062A\u0631" : "Load more";
  let t4;
  if ($[2] !== busy || $[3] !== onMore || $[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <button type="button" className="v2-btn v2-btn--ghost v2-focus" onClick={onMore} disabled={busy} aria-busy={t1}>{t2}{t3}</button>;
    $[2] = busy;
    $[3] = onMore;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== sentinelRef || $[9] !== t4) {
    t5 = <div className="v2-profile-more" ref={sentinelRef}>{t4}</div>;
    $[8] = sentinelRef;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}

/**
 * THE SERVER'S FIRST PAGE SEEDS THE LIST, when it is a real answer.
 *
 * The legacy grids skipped the seed on this endpoint and refetched page one in
 * the browser, so the profile's works were missing from the HTML a crawler
 * reads. `offline_fallback` is the server helper's "I have nothing" marker and
 * must never be presented as an empty profile, so it is not a seed either.
 */
function seedOf<T extends {
  message?: unknown;
  data?: {
    items?: unknown;
  };
}>(data: T | undefined) {
  if (!data || data.message === "offline_fallback" || !Array.isArray(data.data?.items)) return undefined;
  return data;
}
export function ArtistWorksPanel({
  lang,
  dict,
  username,
  initialData
}: {
  lang: Lang;
  dict: Dictionary;
  username: string;
  initialData?: NftsResponse;
}) {
  const grid = useInfiniteGrid<NftItem>({
    apiEndpoint: `/artists/my-nfts/${username}`,
    lang,
    pageSize: 12,
    initialData: seedOf(initialData),
    initialDataUpdatedAt: 0
  });
  const sentinelRef = useInfiniteScrollSentinel({
    hasMore: grid.hasMore,
    isFetching: grid.isFetchingMore,
    onLoadMore: grid.loadMore
  });
  // Whole rows until the list ends (lib/grid-rows); the rest waits for the next batch.
  const cols = useGridColumns();
  const shown = fullRows(grid.items.length, grid.items.length, cols.columns, !grid.hasMore);
  if (grid.isLoading) return <Loading label={dict.states.loading} />;
  if (grid.items.length === 0) {
    return grid.isSuccess ? <DeskEmpty icon={<LayoutGrid />} title={lang === "fa" ? "هنوز اثری نیست" : "No works yet"} body={lang === "fa" ? "این هنرمند هنوز اثری منتشر نکرده است؛ تا آن موقع آثار دیگران را در بازار ببینید." : "This artist has not published any work yet — meanwhile, see what others have in the market."} actions={<Link className="v2-btn v2-btn--ghost v2-focus" href={`/${lang}/market`}>{dict.discovery.browseMarket}</Link>} /> : <Failed dict={dict} onRetry={grid.reset} />;
  }
  return <>
      <div ref={cols.ref} className="v2-grid">
        {grid.items.slice(0, shown).map((item, index) => <NftTile key={item.id} item={item} lang={lang} noMedia={dict.browse.noMedia} marked={index % 4 === 0} />)}
      </div>
      <MoreControl lang={lang} hasMore={grid.hasMore} busy={grid.isFetchingMore} onMore={grid.loadMore} sentinelRef={sentinelRef} />
    </>;
}
export function ArtistCollectionsPanel({
  lang,
  dict,
  username,
  initialData
}: {
  lang: Lang;
  dict: Dictionary;
  username: string;
  initialData?: CollectionsResponse;
}) {
  const grid = useInfiniteGrid<TileCollection>({
    apiEndpoint: `/artists/my-collections/${username}`,
    lang,
    pageSize: 8,
    initialData: seedOf(initialData),
    initialDataUpdatedAt: 0
  });
  const sentinelRef = useInfiniteScrollSentinel({
    hasMore: grid.hasMore,
    isFetching: grid.isFetchingMore,
    onLoadMore: grid.loadMore
  });
  // Whole rows until the list ends (lib/grid-rows); the rest waits for the next batch.
  const cols = useGridColumns();
  const shown = fullRows(grid.items.length, grid.items.length, cols.columns, !grid.hasMore);
  if (grid.isLoading) return <Loading label={dict.states.loading} />;
  if (grid.items.length === 0) {
    return grid.isSuccess ? <DeskEmpty icon={<FolderOpen />} title={lang === "fa" ? "هنوز کالکشنی نیست" : "No collections yet"} body={lang === "fa" ? "این هنرمند هنوز کالکشنی نساخته است." : "This artist has not created a collection yet."} actions={<Link className="v2-btn v2-btn--ghost v2-focus" href={`/${lang}/collection`}>{lang === "fa" ? "دیدن کالکشن‌ها" : "Browse collections"}</Link>} /> : <Failed dict={dict} onRetry={grid.reset} />;
  }
  return <>
      <div ref={cols.ref} className="v2-grid">
        {grid.items.slice(0, shown).map(c => <CollectionTile key={c.id} c={c} lang={lang} sizes="(max-width: 639px) 50vw, (max-width: 1023px) 33vw, 20vw" />)}
      </div>
      <MoreControl lang={lang} hasMore={grid.hasMore} busy={grid.isFetchingMore} onMore={grid.loadMore} sentinelRef={sentinelRef} />
    </>;
}
export function ArtistGalleriesPanel(t0) {
  const $ = _c(16);
  const {
    lang,
    dict,
    userId
  } = t0;
  let t1;
  if ($[0] !== userId) {
    t1 = {
      user_id: userId,
      limit: 24
    };
    $[0] = userId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    galleries,
    isLoading,
    isSuccess,
    refetch
  } = useGalleries(t1);
  if (isLoading) {
    let t2;
    if ($[2] !== dict.states.loading) {
      t2 = <Loading label={dict.states.loading} />;
      $[2] = dict.states.loading;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  if (galleries.length === 0) {
    let t2;
    if ($[4] !== dict || $[5] !== isSuccess || $[6] !== lang || $[7] !== refetch) {
      t2 = isSuccess ? <DeskEmpty icon={<GalleryVerticalEnd />} title={lang === "fa" ? "\u0647\u0646\u0648\u0632 \u06AF\u0627\u0644\u0631\u06CC\u200C\u0627\u06CC \u0646\u06CC\u0633\u062A" : "No galleries yet"} body={lang === "fa" ? "\u0627\u06CC\u0646 \u0647\u0646\u0631\u0645\u0646\u062F \u0647\u0646\u0648\u0632 \u06AF\u0627\u0644\u0631\u06CC\u200C\u0627\u06CC \u0646\u0633\u0627\u062E\u062A\u0647 \u0627\u0633\u062A." : "This artist has not created a gallery yet."} actions={<Link className="v2-btn v2-btn--ghost v2-focus" href={`/${lang}/galleries`}>{lang === "fa" ? "\u06AF\u0634\u062A\u06CC \u062F\u0631 \u06AF\u0627\u0644\u0631\u06CC\u200C\u0647\u0627" : "Browse galleries"}</Link>} /> : <Failed dict={dict} onRetry={() => {
        refetch();
      }} />;
      $[4] = dict;
      $[5] = isSuccess;
      $[6] = lang;
      $[7] = refetch;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    return t2;
  }
  const t2 = galleries as TileGallery[];
  let t3;
  if ($[9] !== lang || $[10] !== t2) {
    let t4;
    if ($[12] !== lang) {
      t4 = g => <GalleryTile key={g.id} g={g} lang={lang} sizes="(max-width: 639px) 25vw, 12vw" />;
      $[12] = lang;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    t3 = t2.map(t4);
    $[9] = lang;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[14] !== t3) {
    t4 = <div className="v2-grid v2-grid--wide">{t3}</div>;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
export function ArtistAuctionsPanel({
  lang,
  dict,
  userId
}: {
  lang: Lang;
  dict: Dictionary;
  userId: number;
}) {
  const grid = useInfiniteGrid<TileAuction>({
    fetcher: ({
      page,
      perPage
    }) => getAuctions({
      user_id: userId,
      limit: perPage,
      page,
      lang
    }),
    // Part of the query key: without it every custom-fetcher list would share
    // one "custom" entry.
    queryParams: {
      scope: "artist-auctions",
      user_id: userId
    },
    lang,
    pageSize: 12
  });
  const sentinelRef = useInfiniteScrollSentinel({
    hasMore: grid.hasMore,
    isFetching: grid.isFetchingMore,
    onLoadMore: grid.loadMore
  });
  // Whole rows until the list ends (lib/grid-rows); the rest waits for the next batch.
  const cols = useGridColumns();
  const shown = fullRows(grid.items.length, grid.items.length, cols.columns, !grid.hasMore);
  if (grid.isLoading) return <Loading label={dict.states.loading} />;
  if (grid.items.length === 0) {
    return grid.isSuccess ? <DeskEmpty icon={<Gavel />} title={lang === "fa" ? "هنوز مزایده‌ای نیست" : "No auctions yet"} body={lang === "fa" ? "این هنرمند هنوز مزایده‌ای برگزار نکرده است." : "This artist has not run an auction yet."} actions={<Link className="v2-btn v2-btn--ghost v2-focus" href={`/${lang}/active-bid`}>{lang === "fa" ? "همه‌ی مزایده‌ها" : "All auctions"}</Link>} /> : <Failed dict={dict} onRetry={grid.reset} />;
  }
  return <>
      <div ref={cols.ref} className="v2-grid">
        {grid.items.slice(0, shown).map(a => <AuctionTile key={a.id} a={a} lang={lang} sizes="(max-width: 639px) 50vw, (max-width: 1023px) 33vw, 20vw" />)}
      </div>
      <MoreControl lang={lang} hasMore={grid.hasMore} busy={grid.isFetchingMore} onMore={grid.loadMore} sentinelRef={sentinelRef} />
    </>;
}

/**
 * The artist's drops, as a visitor sees them: `/drop?user_id=` — the list the owner's
 * «دراپ‌های من» reads, on the same 100 limit — keeping only what a visitor may open
 * (`canViewDrop`, the drops page's own rule), drawn as the drops page's tiles.
 */
export function ArtistDropsPanel({
  lang,
  dict,
  userId
}: {
  lang: Lang;
  dict: Dictionary;
  userId: number;
}) {
  const query = useQuery<Drop[]>({
    queryKey: ["artist-drops", userId, lang],
    queryFn: async () => {
      const res = await dropService.getDrops({
        user_id: userId,
        limit: 100
      }, lang);
      if (res?.message === "offline_fallback") throw new Error("drops unavailable");
      const raw = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.drops) ? res.data.drops : [];
      return raw.map((r: unknown) => normalizeDrop(r as never, lang)).filter((d: Drop) => canViewDrop(d.rawStatus, false));
    },
    enabled: Boolean(userId),
    staleTime: 60_000
  });
  const cols = useGridColumns();
  const drops = query.data ?? [];
  if (query.isLoading) return <Loading label={dict.states.loading} />;
  if (query.isError) return <Failed dict={dict} onRetry={() => {
    void query.refetch();
  }} />;
  if (drops.length === 0) {
    return <DeskEmpty icon={<Sparkles />} title={lang === "fa" ? "هنوز دراپی نیست" : "No drops yet"} body={lang === "fa" ? "این هنرمند هنوز دراپی منتشر نکرده است." : "This artist has not published a drop yet."} actions={<Link className="v2-btn v2-btn--ghost v2-focus" href={`/${lang}/drops`}>{lang === "fa" ? "همه‌ی دراپ‌ها" : "All drops"}</Link>} />;
  }
  return <div ref={cols.ref} className="v2-grid">
      {drops.map(d_0 => <DropTile key={d_0.id} drop={d_0} lang={lang} sizes="(max-width: 639px) 50vw, (max-width: 1023px) 33vw, 20vw" />)}
    </div>;
}